import { CanaryStatus } from '@thyme/libs/src/version/types'

// TYPES --------------------
export type ReportIssueForm = {
  route: string
  // stable keys the API routes on, e.g. 'phone_issue'
  issueTypeKey: string
  subIssueTypeKey: string
  // human-readable labels, sent so Slack stays readable
  issueTypeLabel: string
  subIssueTypeLabel: string
  fullUrl: string | null
  description: string
  reporter: string
  reporterId: string
  checkedDebugSteps: { [key: string]: boolean }
  uiVersion: string
  canaryStatus: CanaryStatus
}

export type ReportIssueDebugCheckboxOption = {
  label: string
  link?: string
}

/**
 * An issue type or sub-issue type option. `key` is the stable identifier the
 * API matches on to route reports to assignees — never change a key to fix
 * wording, change the `label` instead.
 */
export type ReportIssueTypeOption = {
  key: string
  label: string
  subTypes?: ReportIssueTypeOption[]
}

// CONSTANTS ----------------

const OTHER_ISSUE_TYPE = { key: 'other', label: 'Other' }

export const DEFAULT_PATIENT_PROFILE_ISSUE_TYPES: ReportIssueTypeOption[] = [
  { key: 'profile_not_loading', label: 'Profile not loading' },
  {
    key: 'playbook_or_task_unexpected',
    label: 'Playbook or Task was added, not added, or cancelled unexpectedly',
  },
  {
    key: 'phone_issue',
    label: 'Phone issue',
    subTypes: [
      { key: 'phone_audio_dropped', label: 'Audio dropped' },
      { key: 'phone_outbound_not_dialing', label: "Outbound didn't dial" },
      { key: 'phone_error_message', label: 'Error message' },
      { key: 'phone_inbound_not_ringing', label: "Inbound didn't ring" },
      { key: 'phone_poor_audio_quality', label: 'Poor audio quality' },
    ],
  },
  {
    key: 'texting_issue',
    label: 'Texting issue',
    subTypes: [
      {
        key: 'texting_duplicate_messages',
        label: 'Message sent multiple times',
      },
      { key: 'texting_not_going_through', label: 'Text not going through' },
      { key: 'texting_failed_status', label: "Text says 'failed'" },
      { key: 'texting_member_not_consented', label: 'Member not consented' },
      {
        key: 'texting_shared_number',
        label: 'Shared number preventing texting',
      },
    ],
  },
  { key: 'program_status_issue', label: 'Program status issue' },
  {
    key: 'documentation_issue',
    label: 'Subtask or communications documentation issue',
    subTypes: [
      { key: 'documentation_form', label: 'Form issue' },
      { key: 'documentation_notes', label: 'Notes issue' },
      { key: 'documentation_subtask_status', label: 'Subtask status issue' },
      {
        key: 'documentation_communication_status',
        label: 'Communication status issue',
      },
      {
        key: 'documentation_subtask_communication_link',
        label: 'Issue linking subtasks to communication',
      },
    ],
  },
  {
    key: 'member_sidebar_display_issue',
    label: 'Member sidebar display issue',
  },
  { key: 'care_plan_issue', label: 'Care plan issue' },
  OTHER_ISSUE_TYPE,
]

export const DEFAULT_QUEUE_ISSUE_TYPES: ReportIssueTypeOption[] = [
  { key: 'queue_not_loading', label: 'Queue not loading' },
  { key: 'queue_display_issue', label: 'Queue display issue' },
  { key: 'filters_not_working', label: 'Filters not working' },
  OTHER_ISSUE_TYPE,
]

export const DEFAULT_BASIC_ISSUE_TYPES: ReportIssueTypeOption[] = [
  { key: 'page_not_loading', label: 'Page not loading' },
  { key: 'search_issue', label: 'Search issue' },
  { key: 'filters_not_working', label: 'Filters not working' },
  OTHER_ISSUE_TYPE,
]

export const DEFAULT_DEBUG_STEPS = [
  {
    label: 'I cleared my browser cache',
    link: 'https://support.google.com/accounts/answer/32050',
  },
  {
    label: 'I confirmed that my VPN is connected',
    link: 'https://app.gitbook.com/o/-MbN892xoO-71tzzZ9Yf/s/-MkCyG1YxEnT-aLhAhNp/openvpn-connect/import-vpn-profile-to-openvpn',
  },
  { label: 'I logged out and back into my Gmail account' },
]

const QUEUE_PAGES = [
  '#myQueue',
  '#enrollmentQueue',
  '#podLeadQueue',
  '#enrollmentLeadQueue',
]

const PATIENT_PROFILE_PAGES = [
  '#patientProfile',
  '#patientProfileCarePlan',
  '#patientProfileClinicalSummary',
  '#patientProfileSocialSummary',
  '#patientProfileHistory',
  '#patientProfileToDo',
  '#patientProfileSymptoms',
  '#patientProfileTexting',
  '#patientProfileMedicalContext',
  '#patientProfileDocuments',
]

// IMPROVEME(MT-3265): post-pilot, add Admin pages
const BASIC_PAGES = ['#allTasks', '#patients', '#adminUsers']

/**
 * Builds a page name -> issue type options lookup.
 * @param pages
 * @param options
 */
const toIssueTypesByPage = (
  pages: string[],
  options: ReportIssueTypeOption[]
): Record<string, ReportIssueTypeOption[]> =>
  Object.fromEntries(pages.map((page) => [page, options]))

export const reportIssueTypesForRouteMap: Record<
  string,
  ReportIssueTypeOption[]
> = {
  ...toIssueTypesByPage(QUEUE_PAGES, DEFAULT_QUEUE_ISSUE_TYPES),
  ...toIssueTypesByPage(
    PATIENT_PROFILE_PAGES,
    DEFAULT_PATIENT_PROFILE_ISSUE_TYPES
  ),
  ...toIssueTypesByPage(BASIC_PAGES, DEFAULT_BASIC_ISSUE_TYPES),
  // Default/catch-all case
  '': [OTHER_ISSUE_TYPE],
}
